import { differenceInCalendarDays, differenceInDays, format, parseISO, subDays } from 'date-fns';
/**
 *
 * @param date
 * @returns formatted Date string in the form of `yyyy-MM-dd`
 */
export function simpleDashDate(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  return format(pdate, 'yyyy-MM-dd');
}
/**
 *
 * @param date
 * @returns formatted Date string in the form of `MM/dd/yyyy`
 */
export function simpleDate(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  return format(pdate, 'MM/dd/yyyy');
}

/**
 * Formats to a date string with time
 * @param date
 * @returns A string containing the date and time. Example: "04/10/2024 at 12:00 AM"
 */
export const simpleDateAtTime = (date: string | Date) => {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;

  return format(pdate, "MM/dd/yyyy 'at' h:mm a");
};

// With seconds — for feeds where many events land inside a single minute
// (e.g. the referral activity log's automated bursts).
export const simpleDateAtTimeWithSeconds = (date: string | Date) => {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;

  return format(pdate, "MM/dd/yyyy 'at' h:mm:ss a");
};

/**
 * @param date
 * @returns formatted Date string depending on the relative difference between the date and the current date
 */
export function dateAtTime(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  const reldif = differenceInCalendarDays(pdate, new Date());
  if (reldif == 0) {
    return format(pdate, "'Today at' h:mm a");
  }
  if (reldif == 1) {
    return format(pdate, "'Tomorrow at' h:mm a");
  }
  if (reldif == -1) {
    return format(pdate, "'Yesterday at' h:mm a");
  }
  return format(pdate, "MM/dd/yyyy 'at' h:mm a");
}
/**
 *
 * @param date
 * @returns formatted Date string in the form of `MM/dd/yyyy 'at' h:mm a`
 */
export function dateAtTimeNoRelative(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  return format(pdate, "MM/dd/yyyy 'at' h:mm a");
}

export function dateWithoutTimezone(date?: string | Date | null) {
  return simpleDate(date);
  //need more information about what the difference is supposed to be,
  //since neither function printed a timezone.
}
/**
 * @param date
 * @returns formatted Date string in the form of `yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`
 */
export function parseDate(date?: string | Date | null) {
  if (!date) return null;
  if (date instanceof Date) return date;
  try {
    const parsed = parseISO(date);
    if (isNaN(parsed.getTime())) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param start
 * @param end
 * @returns object with start and end dates for the prior period
 */
export function calculatePriorPeriod(start: Date, end: Date) {
  const priorEnd = subDays(start, 1); // one day before start of current period
  const priorStart = subDays(priorEnd, differenceInDays(end, start)); // same difference in days as current period

  return {
    start: priorStart,
    end: priorEnd,
  };
}

// "12 min" under an hour; "1 hr 3 mins" beyond. Coarse on purpose — it's an at-a-glance age.
export function elapsed(date: Date, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min${rest === 1 ? '' : 's'}`;
}
